import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentUser } from '../services/authService';
import { getUserConversations, subscribeConversationUnreadCount } from '../services/chatService';
import { useWindowSize, BP } from '../hooks/useWindowSize';

function ChatListPage() {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < BP.mobile;
  const user = getCurrentUser();
  const [conversations, setConversations] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [workerNames, setWorkerNames] = useState({});
  const [sortBy, setSortBy] = useState('recent'); // 'recent' or 'jobName'

  useEffect(() => {
    if (!user) return;
    const countUnsubs = [];

    const unsubscribe = getUserConversations(user.uid, async (convs) => {
      setConversations(convs);
      setLoading(false);

      // Cancel previous count listeners
      countUnsubs.forEach(fn => fn());
      countUnsubs.length = 0;

      // Subscribe to real-time unread count per conversation
      for (const conv of convs) {
        const unsub = subscribeConversationUnreadCount(conv.id, user.uid, (count) => {
          setUnreadCounts(prev => ({ ...prev, [conv.id]: count }));
        });
        countUnsubs.push(unsub);
      }

      // Fetch worker display names for all conversations — HC-INFRA-008C,
      // Prompt #422: reads the public projection (publicWorkerProfiles/{uid},
      // Prompt #419), not the raw users/{uid} doc. displayName/photoURL are
      // the only name/photo fields this projection ever carries (no
      // firstName, no email). A real, small fix made while here: the
      // pre-migration code only set a 'Worker' fallback when the doc
      // existed but displayName was falsy — a genuinely-missing doc (no
      // projection yet, e.g. onboarding incomplete) left nameMap[conv.id]
      // unset entirely, rendering as a blank name (never a crash, but not
      // a real placeholder either). Now explicitly falls back to 'Worker'
      // in both cases.
      const nameMap = {};
      await Promise.all(convs.map(async (conv) => {
        if (conv.candidateId) {
          try {
            const workerDoc = await getDoc(doc(db, 'publicWorkerProfiles', conv.candidateId));
            if (workerDoc.exists()) {
              const d = workerDoc.data();
              nameMap[conv.id] = d.displayName || 'Worker';
              if (d.photoURL) nameMap[`${conv.id}_photo`] = d.photoURL;
            } else {
              nameMap[conv.id] = 'Worker';
            }
          } catch (_) {
            nameMap[conv.id] = 'Worker';
          }
        }
      }));
      setWorkerNames(nameMap);
    });

    return () => {
      unsubscribe();
      countUnsubs.forEach(fn => fn());
    };
  }, [user]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  return (
    <>
      {/* Sort control */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>Sort by:</span>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {[
            { key: 'recent', label: 'Newest' },
            { key: 'jobName', label: 'Job Name' },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => setSortBy(opt.key)}
              style={{
                padding: isMobile ? '6px 10px' : '4px 10px',
                fontSize: '12px',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: sortBy === opt.key ? '#E8590C' : '#e5e7eb',
                backgroundColor: sortBy === opt.key ? '#fff5f0' : 'white',
                color: sortBy === opt.key ? '#E8590C' : '#6b7280',
                cursor: 'pointer',
                fontWeight: sortBy === opt.key ? '600' : '400',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Conversations List */}
      <div style={{ width: '100%', overflowX: 'hidden', maxWidth: '100vw' }}>
        <div style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
              Loading conversations...
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <p style={{ color: '#6b7280', fontSize: '18px' }}>No conversations yet</p>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginTop: '0.5rem' }}>
                Conversations will appear when candidates apply to your jobs
              </p>
            </div>
          ) : (
            (() => {
              const displayedConversations = sortBy === 'jobName'
                ? [...conversations].sort((a, b) => (a.jobTitle || '').localeCompare(b.jobTitle || ''))
                : conversations; // already sorted by lastMessageTime desc from Firestore
              return displayedConversations.map(conv => {
              const unreadCount = unreadCounts[conv.id] || 0;
              
              return (
                <div
                  key={conv.id}
                  onClick={() => navigate(`/chat/${conv.id}`)}
                  style={{
                    padding: isMobile ? '1rem' : '1rem 1.5rem',
                    minHeight: isMobile ? '64px' : undefined,
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    position: 'relative',
                    overflow: 'hidden',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: unreadCount > 0 ? '#fff3eb' : '#F8FAFC',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = unreadCount > 0 ? '#fff3eb' : '#F8FAFC'}
                >
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    backgroundColor: '#E8590C',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '700',
                    color: 'white',
                    flexShrink: 0,
                    position: 'relative',
                  }}>
                    {workerNames[`${conv.id}_photo`] ? (
                      <img src={workerNames[`${conv.id}_photo`]} alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', overflow: 'hidden' }} />
                    ) : (
                      <span>{(workerNames[conv.id] || '?')[0].toUpperCase()}</span>
                    )}
                    {unreadCount > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '-3px',
                        right: '-3px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        borderRadius: '50%',
                        minWidth: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        padding: '0 3px',
                        border: '2px solid white',
                      }}>
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2px' }}>
                      <div style={{ fontWeight: unreadCount > 0 ? '700' : '600', fontSize: '14px', color: '#0F1E2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {conv.jobTitle || 'Conversation'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', flexShrink: 0, marginLeft: '8px' }}>
                        {formatTime(conv.lastMessageTime)}
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {workerNames[conv.id] || ''}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: unreadCount > 0 ? '#111827' : '#6b7280',
                      fontWeight: unreadCount > 0 ? '500' : '400',
                      display: 'block',
                      width: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {typeof conv.lastMessage === 'string'
                        ? conv.lastMessage
                        : conv.lastMessage?.text || 'No messages yet'}
                    </div>
                  </div>
                </div>
              );
            });
            })()
          )}
        </div>
      </div>
    </>
  );
}

export default ChatListPage;